import { Branded } from 'types'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseArray, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

/**
 * Signoff role enum
 */
export enum SignoffRole {
  SUPERVISOR = 'supervisor',
  DATA_ANALYST = 'dataAnalyst',
  REVIEWER = 'reviewer',
}

/**
 * Digital signature data
 */
export interface SignatureData {
  role: SignoffRole
  name: string
  email?: string
  qualifications: string
  timestamp: string
  signature?: string
}

/**
 * Report signoff state
 */
export interface SignoffState {
  supervisorSignoff?: SignatureData | null
  reviewerSignoff?: SignatureData | null
  dataAnalystSignoff?: SignatureData | null
  lastRevision: number
}

/**
 * Metadata about a report revision
 */
export interface RevisionMetadata {
  revisionNumber: number
  createdAt: string
  publishedAt?: string
  isPublished: boolean
}

/**
 * Lock state for a report
 */
export interface LockState {
  isLocked: boolean
  startedSignoff: boolean
  lastLockTimestamp?: string
}

export interface ApprovalUserRef {
  userId: string
  name: string
  email: string
}

export interface ApprovalAssignment extends ApprovalUserRef {
  assignedAt: string
  qualifications?: string
}

/**
 * Who may sign which role on a report revision. Roles are chosen per report;
 * enforcement compares the authenticated signer against `assignments[role]`.
 */
export interface ReportApprovalWorkflow {
  configuredBy: ApprovalUserRef
  configuredAt: string
  requiredRoles: SignoffRole[]
  assignments: Partial<Record<SignoffRole, ApprovalAssignment>>
}

export type ESignatureMethod = 'typed' | 'drawn' | 'uploaded'

/**
 * Audit record of one signing. The signature image is never stored here: it
 * exists only inside the signed PDF at `signedDocumentPath`. `signerName` is
 * the authenticated account name; `signatureText` is whatever the signer chose
 * to type, which may differ.
 */
export interface ReportESignatureRecord {
  id: string
  role: SignoffRole
  signerId: string
  signerName: string
  signerEmail: string
  qualifications?: string
  method: ESignatureMethod
  signatureText?: string
  typedFont?: string
  signedAt: string
  sourceDocumentPath: string
  sourceDocumentSha256: string
  signedDocumentPath: string
  signedDocumentSha256: string
  userAgent: string
}

export interface ReportESignatureState {
  records: ReportESignatureRecord[]
}

export type SectionId = Branded<string, 'SectionId'>

export type GeckoReportingMetadataJSON = {
  reportId: string // Since reports will have multiple revisions, we need to store an idenfitier for the report being generated
  reportTemplateId: string
  sectionInfo: Record<SectionId, Record<string, unknown>>
  clientProvidedImages?: Record<string, unknown>
  assetDrawings?: {
    images: Record<string, unknown>
  }
  signoff?: SignoffState
  approvalWorkflow?: ReportApprovalWorkflow
  eSignatures?: ReportESignatureState
  revisionMetadata: RevisionMetadata
  publishedPdfPath?: string
  inspections: string[]
  visualInspections: string[]
  lockState?: LockState
}

/**
 * Base type for data needed to generate reports
 * This is primarily used for reporting the results of inspections.
 * The contents of each section are dependent on the inspection type and the selected inspection template.
 * Reports may include multiple inspections.
 */
export class GeckoReportingMetadata extends GeckoEntryBase<
  GeckoReportingMetadataJSON,
  {
    organization: string
    site: string
    [RolodexEntryType.UNIT]: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        reportId: parseString(json.data.report_id) ?? '',
        reportTemplateId: parseString(json.data.report_template_id) ?? '',
        sectionInfo: json.data.section_info as Record<
          SectionId,
          Record<string, unknown>
        >,
        clientProvidedImages: json.data.client_provided_images as Record<
          string,
          unknown
        >,
        assetDrawings: json.data.asset_drawings as {
          images: Record<string, unknown>
        },
        signoff: json.data.signoff as SignoffState | undefined,
        approvalWorkflow: json.data.approval_workflow as
          | ReportApprovalWorkflow
          | undefined,
        eSignatures: json.data.e_signatures as
          | ReportESignatureState
          | undefined,
        revisionMetadata: json.data.revision_metadata as RevisionMetadata,
        publishedPdfPath: parseString(json.data.published_pdf_path),
        inspections: parseArray<string>(json.data.inspections) ?? [],
        visualInspections:
          parseArray<string>(json.data.visual_inspections) ?? [],
        lockState: json.data.lock_state as LockState | undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        [RolodexEntryType.UNIT]:
          parseString(json.tags[RolodexEntryType.UNIT]) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoReportingMetadata {
    return new GeckoReportingMetadata(json)
  }
}
